import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const SMART_DEFAULTS = [
  "networking",
  "follow-up",
  "application",
  "interview-prep",
  "research",
  "portfolio",
  "resume",
  "cover-letter",
  "salary-negotiation",
  "references"
];

interface TagSelectorProps {
  value: string[];
  onChange: (tags: string[]) => void;
  userTags?: string[];
  disabled?: boolean;
}

export const TagSelector: React.FC<TagSelectorProps> = ({ value, onChange, userTags = [], disabled }) => {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);

  useEffect(() => {
    // Show smart defaults and user tags not already selected
    const all = Array.from(new Set([...SMART_DEFAULTS, ...userTags]))
      .filter(tag => !value.includes(tag.toLowerCase()));
    setSuggestions(all);
  }, [userTags, value]);

  const addTag = (tag: string) => {
    const normalized = tag.trim().toLowerCase();
    if (normalized && !value.includes(normalized)) {
      onChange([...value, normalized]);
    }
    setInput("");
  };

  const removeTag = (tag: string) => {
    onChange(value.filter(t => t !== tag));
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && input.trim()) {
      addTag(input);
      e.preventDefault();
    }
    if (e.key === "Backspace" && !input && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map(tag => (
          <Badge key={tag} variant="secondary" className="flex items-center gap-1">
            {tag}
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-4 w-4 p-0 ml-1"
              onClick={() => removeTag(tag)}
              disabled={disabled}
            >
              ×
            </Button>
          </Badge>
        ))}
      </div>
      <Input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleInputKeyDown}
        placeholder="Add tag..."
        disabled={disabled}
        className="mb-2"
      />
      <div className="flex flex-wrap gap-2">
        {suggestions.map(tag => (
          <Button
            key={tag}
            type="button"
            size="sm"
            variant="outline"
            className="text-xs"
            onClick={() => addTag(tag)}
            disabled={disabled}
          >
            {tag}
          </Button>
        ))}
      </div>
    </div>
  );
};
